/**
 * Subscription Actions
 * 
 * Redux actions for the new subscription flow supporting both
 * Stripe and Paystack payment providers.
 */

import {
  SET_SUBSCRIPTION_LOADING,
  SET_CURRENT_SUBSCRIPTION,
  SET_AVAILABLE_PLANS,
  SET_SUBSCRIPTION_ERROR,
  CLEAR_SUBSCRIPTION_ERROR,
  SET_PROMO_CODE_STATUS,
  SET_CHECKOUT_SESSION,
  SET_PAYMENT_PROVIDER,
  SET_SELECTED_PLAN,
  RESET_SUBSCRIPTION_STATE,
  SUBSCRIPTION_STATUS,
} from "../constants";
import api from "../../api";

// ==========================================
// LOADING & ERROR ACTIONS
// ==========================================

export const setSubscriptionLoading = (loading) => (dispatch) => {
  dispatch({
    type: SET_SUBSCRIPTION_LOADING,
    payload: loading,
  });
};

export const setSubscriptionError = (error) => (dispatch) => {
  dispatch({
    type: SET_SUBSCRIPTION_ERROR,
    payload: error,
  });
};

export const clearSubscriptionError = () => (dispatch) => {
  dispatch({
    type: CLEAR_SUBSCRIPTION_ERROR,
  });
};

export const resetSubscriptionState = () => (dispatch) => {
  dispatch({
    type: RESET_SUBSCRIPTION_STATE,
  });
};

// ==========================================
// SUBSCRIPTION FETCHING ACTIONS
// ==========================================

/**
 * Fetch the current active subscription for the merchant
 */
export const fetchCurrentSubscription = () => async (dispatch) => {
  dispatch(setSubscriptionLoading(true));

  try {
    const response = await api.subscription.getCurrentSubscription();
    
    dispatch({
      type: SET_CURRENT_SUBSCRIPTION,
      payload: response.data.subscription,
    });

    // Update subscription status in renewal reducer
    if (response.data.subscription?.status === "active") {
      dispatch({
        type: SUBSCRIPTION_STATUS,
        payload: "active",
      });
    } else if (response.data.subscription?.status) {
      dispatch({
        type: SUBSCRIPTION_STATUS,
        payload: response.data.subscription.status,
      });
    } else {
      dispatch({
        type: SUBSCRIPTION_STATUS,
        payload: "not_active",
      });
    }

    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.error || error.message || "Failed to fetch subscription";
    dispatch(setSubscriptionError(errorMessage));
    
    dispatch({
      type: SUBSCRIPTION_STATUS,
      payload: "not_active",
    });
    
    return null;
  } finally {
    dispatch(setSubscriptionLoading(false));
  }
};

/**
 * Fetch available subscription plans
 */
export const fetchAvailablePlans = () => async (dispatch) => {
  dispatch(setSubscriptionLoading(true));

  try {
    const response = await api.subscription.getAvailablePlans();
    
    dispatch({
      type: SET_AVAILABLE_PLANS,
      payload: response.data,
    });

    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.error || error.message || "Failed to fetch plans";
    dispatch(setSubscriptionError(errorMessage));
    return null;
  } finally {
    dispatch(setSubscriptionLoading(false));
  }
};

// ==========================================
// PROMO CODE ACTIONS
// ==========================================

/**
 * Validate a promo code
 * @param {string} promoCode - The promo code to validate
 */
export const validatePromoCode = (promoCode) => async (dispatch) => {
  dispatch(setSubscriptionLoading(true));

  try {
    const response = await api.subscription.validatePromoCode(promoCode);
    
    dispatch({
      type: SET_PROMO_CODE_STATUS,
      payload: {
        valid: response.data.valid,
        promoCode: response.data.promo_code,
        discountPercent: response.data.discount_percent,
        message: response.data.message,
      },
    });

    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.error || "Invalid promo code";
    
    dispatch({
      type: SET_PROMO_CODE_STATUS,
      payload: {
        valid: false,
        promoCode,
        error: errorMessage,
      },
    });

    return { valid: false, error: errorMessage };
  } finally {
    dispatch(setSubscriptionLoading(false));
  }
};

/**
 * Clear promo code status
 */
export const clearPromoCodeStatus = () => (dispatch) => {
  dispatch({
    type: SET_PROMO_CODE_STATUS,
    payload: null,
  });
};

// ==========================================
// STRIPE SUBSCRIPTION ACTIONS
// ==========================================

/**
 * Create a Stripe checkout session
 * @param {Object} data - Checkout data
 */
export const createStripeCheckout = (data) => async (dispatch) => {
  dispatch(setSubscriptionLoading(true));
  dispatch(clearSubscriptionError());

  try {
    const response = await api.subscription.createCheckoutSession(data);
    
    dispatch({
      type: SET_CHECKOUT_SESSION,
      payload: response.data,
    });

    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.error || error.message || "Failed to create checkout session";
    dispatch(setSubscriptionError(errorMessage));
    return null;
  } finally {
    dispatch(setSubscriptionLoading(false));
  }
};

/**
 * Create a Stripe subscription directly
 * @param {Object} data - Subscription data
 */
export const createStripeSubscription = (data) => async (dispatch) => {
  dispatch(setSubscriptionLoading(true));
  dispatch(clearSubscriptionError());

  try {
    const response = await api.subscription.createStripeSubscription(data);
    
    dispatch({
      type: SET_CURRENT_SUBSCRIPTION,
      payload: response.data.subscription,
    });

    dispatch({
      type: SUBSCRIPTION_STATUS,
      payload: response.data.status || "active",
    });

    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.error || error.message || "Failed to create subscription";
    dispatch(setSubscriptionError(errorMessage));
    return null;
  } finally {
    dispatch(setSubscriptionLoading(false));
  }
};

/**
 * Cancel a Stripe subscription
 * @param {string|number} subscriptionId - Subscription ID
 * @param {boolean} immediately - Cancel immediately or at period end
 * @param {string} reason - Cancellation reason
 */
export const cancelSubscription = (subscriptionId, immediately = false, reason = null) => async (dispatch) => {
  dispatch(setSubscriptionLoading(true));
  dispatch(clearSubscriptionError());

  try {
    const response = await api.subscription.cancelSubscription(subscriptionId, immediately, reason);
    
    dispatch({
      type: SET_CURRENT_SUBSCRIPTION,
      payload: response.data.subscription,
    });

    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.error || error.message || "Failed to cancel subscription";
    dispatch(setSubscriptionError(errorMessage));
    return null;
  } finally {
    dispatch(setSubscriptionLoading(false));
  }
};

/**
 * Reactivate a cancelled subscription
 * @param {string|number} subscriptionId - Subscription ID
 */
export const reactivateSubscription = (subscriptionId) => async (dispatch) => {
  dispatch(setSubscriptionLoading(true));
  dispatch(clearSubscriptionError());

  try {
    const response = await api.subscription.reactivateSubscription(subscriptionId);
    
    dispatch({
      type: SET_CURRENT_SUBSCRIPTION,
      payload: response.data.subscription,
    });

    dispatch({
      type: SUBSCRIPTION_STATUS,
      payload: "active",
    });

    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.error || error.message || "Failed to reactivate subscription";
    dispatch(setSubscriptionError(errorMessage));
    return null;
  } finally {
    dispatch(setSubscriptionLoading(false));
  }
};

/**
 * Upgrade subscription
 * @param {string|number} subscriptionId - Subscription ID
 * @param {Object} data - Upgrade data
 */
export const upgradeSubscription = (subscriptionId, data) => async (dispatch) => {
  dispatch(setSubscriptionLoading(true));
  dispatch(clearSubscriptionError());

  try {
    const response = await api.subscription.upgradeSubscription(subscriptionId, data);
    
    // Refresh current subscription
    dispatch(fetchCurrentSubscription());

    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.error || error.message || "Failed to upgrade subscription";
    dispatch(setSubscriptionError(errorMessage));
    return null;
  } finally {
    dispatch(setSubscriptionLoading(false));
  }
};

// ==========================================
// PAYSTACK SUBSCRIPTION ACTIONS
// ==========================================

/**
 * Initialize Paystack payment
 * @param {Object} data - Payment data
 */
export const initializePaystackPayment = (data) => async (dispatch) => {
  dispatch(setSubscriptionLoading(true));
  dispatch(clearSubscriptionError());

  console.log("initializePaystackPayment called with data:", data);

  try {
    const response = await api.subscription.initializePaystack(data);
    console.log("initializePaystack API response:", response);
    
    dispatch({
      type: SET_CHECKOUT_SESSION,
      payload: {
        ...response.data,
        provider: "paystack",
      },
    });

    console.log("✓ Payment initialized, reference:", response.data?.reference);
    return response.data;
  } catch (error) {
    console.error("initializePaystack error:", {
      error,
      response: error.response,
      status: error.response?.status,
      data: error.response?.data,
    });
    const errorMessage = error.response?.data?.error || error.message || "Failed to initialize payment";
    dispatch(setSubscriptionError(errorMessage));
    return null;
  } finally {
    dispatch(setSubscriptionLoading(false));
  }
};

/**
 * Verify Paystack payment
 * @param {string} reference - Payment reference
 */
export const verifyPaystackPayment = (reference) => async (dispatch) => {
  dispatch(setSubscriptionLoading(true));
  dispatch(clearSubscriptionError());

  console.log("verifyPaystackPayment called with reference:", reference);

  try {
    const response = await api.subscription.verifyPaystackPayment(reference);
    console.log("verifyPaystackPayment API response:", response);
    
    // Check both 'success' and 'status' fields
    const isSuccess = response.data.success === true || response.data.status === "success";
    console.log("API response success check:", {
      success: response.data.success,
      status: response.data.status,
      isSuccess: isSuccess
    });

    if (isSuccess) {
      console.log("✓ Payment verification successful");
      dispatch({
        type: SET_CURRENT_SUBSCRIPTION,
        payload: response.data.subscription,
      });

      dispatch({
        type: SUBSCRIPTION_STATUS,
        payload: "active",
      });
    }

    return response.data;
  } catch (error) {
    console.error("verifyPaystackPayment error:", {
      error,
      response: error.response,
      status: error.response?.status,
      data: error.response?.data,
    });
    const errorMessage = error.response?.data?.error || error.message || "Payment verification failed";
    dispatch(setSubscriptionError(errorMessage));
    return null;
  } finally {
    dispatch(setSubscriptionLoading(false));
  }
};

/**
 * Cancel Paystack subscription
 * Paystack requires a two-step process with email token:
 * - First call with token=null triggers email to user
 * - Second call with token from email completes cancellation
 * @param {string|number} subscriptionId - Subscription ID
 * @param {string|null} token - Email token (null on first call, token string on second call)
 */
export const cancelPaystackSubscription = (subscriptionId, token = null) => async (dispatch) => {
  dispatch(setSubscriptionLoading(true));
  dispatch(clearSubscriptionError());

  try {
    const response = await api.subscription.cancelPaystackSubscription(subscriptionId, token);
    
    // If subscription is fully cancelled, update state
    if (response.data.subscription) {
      dispatch({
        type: SET_CURRENT_SUBSCRIPTION,
        payload: response.data.subscription,
      });
    }

    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.error || error.message || "Failed to cancel subscription";
    dispatch(setSubscriptionError(errorMessage));
    return null;
  } finally {
    dispatch(setSubscriptionLoading(false));
  }
};

/**
 * Preview Paystack subscription upgrade (proration calculation)
 * @param {Object} data - Upgrade data
 * @param {number} data.add_staff - Number of staff to add
 * @param {number} data.add_branches - Number of branches to add
 */
export const previewPaystackUpgrade = (data) => async (dispatch) => {
  dispatch(setSubscriptionLoading(true));
  dispatch(clearSubscriptionError());

  try {
    const response = await api.subscription.previewPaystackUpgrade(data);
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.error || error.message || "Failed to preview upgrade";
    dispatch(setSubscriptionError(errorMessage));
    return null;
  } finally {
    dispatch(setSubscriptionLoading(false));
  }
};

/**
 * Add staff to Paystack subscription (with proration)
 * @param {string|number} subscriptionId - Subscription ID
 * @param {number} quantity - Number of staff to add
 */
export const addPaystackStaff = (subscriptionId, quantity) => async (dispatch) => {
  dispatch(setSubscriptionLoading(true));
  dispatch(clearSubscriptionError());

  try {
    const response = await api.subscription.addPaystackStaff(subscriptionId, quantity);
    
    // Refresh subscription data
    dispatch(fetchCurrentSubscription());
    
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.error || error.message || "Failed to add staff";
    dispatch(setSubscriptionError(errorMessage));
    return null;
  } finally {
    dispatch(setSubscriptionLoading(false));
  }
};

/**
 * Add branches to Paystack subscription (with proration)
 * @param {string|number} subscriptionId - Subscription ID
 * @param {number} quantity - Number of branches to add
 */
export const addPaystackBranches = (subscriptionId, quantity) => async (dispatch) => {
  dispatch(setSubscriptionLoading(true));
  dispatch(clearSubscriptionError());

  try {
    const response = await api.subscription.addPaystackBranches(subscriptionId, quantity);
    
    // Refresh subscription data
    dispatch(fetchCurrentSubscription());
    
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.error || error.message || "Failed to add branches";
    dispatch(setSubscriptionError(errorMessage));
    return null;
  } finally {
    dispatch(setSubscriptionLoading(false));
  }
};

// ==========================================
// PLAN SELECTION ACTIONS
// ==========================================

/**
 * Set the selected plan
 * @param {Object} plan - Selected plan object
 */
export const setSelectedPlan = (plan) => (dispatch) => {
  dispatch({
    type: SET_SELECTED_PLAN,
    payload: plan,
  });
};

/**
 * Set payment provider
 * @param {string} provider - "stripe" or "paystack"
 */
export const setPaymentProvider = (provider) => (dispatch) => {
  dispatch({
    type: SET_PAYMENT_PROVIDER,
    payload: provider,
  });
};

/**
 * Determine and set payment provider based on currency
 * @param {string} currency - User's currency code
 */
export const determinePaymentProvider = (currency) => (dispatch) => {
  const provider = api.subscription.getPaymentProvider(currency);
  
  dispatch({
    type: SET_PAYMENT_PROVIDER,
    payload: provider,
  });

  return provider;
};

// ==========================================
// ADDON ACTIONS
// ==========================================

/**
 * Add staff to subscription
 * @param {string|number} subscriptionId - Subscription ID
 * @param {number} quantity - Number of staff to add
 * @param {string} provider - "stripe" or "paystack"
 */
export const addStaffToSubscription = (subscriptionId, quantity, provider = "stripe") => async (dispatch) => {
  dispatch(setSubscriptionLoading(true));
  dispatch(clearSubscriptionError());

  try {
    let response;
    if (provider === "paystack") {
      response = await api.subscription.addPaystackStaff(subscriptionId, quantity);
    } else {
      response = await api.subscription.addStaff(subscriptionId, quantity);
    }
    
    // Refresh current subscription
    dispatch(fetchCurrentSubscription());

    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.error || error.message || "Failed to add staff";
    dispatch(setSubscriptionError(errorMessage));
    return null;
  } finally {
    dispatch(setSubscriptionLoading(false));
  }
};

/**
 * Add branch to subscription
 * @param {string|number} subscriptionId - Subscription ID
 * @param {number} quantity - Number of branches to add
 * @param {string} provider - "stripe" or "paystack"
 */
export const addBranchToSubscription = (subscriptionId, quantity, provider = "stripe") => async (dispatch) => {
  dispatch(setSubscriptionLoading(true));
  dispatch(clearSubscriptionError());

  try {
    let response;
    if (provider === "paystack") {
      response = await api.subscription.addPaystackBranches(subscriptionId, quantity);
    } else {
      response = await api.subscription.addBranch(subscriptionId, quantity);
    }
    
    // Refresh current subscription
    dispatch(fetchCurrentSubscription());

    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.error || error.message || "Failed to add branch";
    dispatch(setSubscriptionError(errorMessage));
    return null;
  } finally {
    dispatch(setSubscriptionLoading(false));
  }
};
